import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Textarea from 'react-textarea-autosize';
import isEqual from 'lodash/isEqual';
import { Award } from 'lucide-react';

import EntryDialog from 'components/Portfolio/EntryDialog/EntryDialog';
import {
  CharCount,
  Field,
  YearSelect,
  inputClass,
} from 'components/Portfolio/EntryDialog/EntryFields';
import fieldStyles from 'components/Portfolio/EntryDialog/EntryDialog.module.scss';
import { fromYear, toYear } from 'helpers/careerEntries';
import { getErrorMessage } from 'helpers/apiErrors';

const MAX_DESCRIPTION = 3000;

const toDraft = (education) => ({
  school: education?.organization || '',
  course: education?.qualification || '',
  from: toYear(education?.started_at),
  to: toYear(education?.ended_at),
  content: education?.content || '',
});

const EducationDialog = ({
  show,
  education,
  saving,
  onSave,
  onDelete,
  onClose,
  onAddQualification,
}) => {
  const [initial] = useState(() => toDraft(education));
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState('');
  const isEdit = !!education;

  const set = (changes) => setDraft((current) => ({ ...current, ...changes }));

  const endBeforeStart =
    draft.from !== '' && draft.to !== '' && draft.to < draft.from
      ? 'The end year can’t be before the start year.'
      : '';
  const dirty = !isEqual(draft, initial);
  const canSave =
    dirty &&
    !!draft.school.trim() &&
    !!draft.course.trim() &&
    draft.from !== '' &&
    draft.to !== '' &&
    !endBeforeStart;

  const handleSave = () => {
    setError('');
    onSave({
      ...education,
      organization: draft.school.trim(),
      qualification: draft.course.trim(),
      content: draft.content,
      started_at: fromYear(draft.from, education?.started_at),
      ended_at: fromYear(draft.to, education?.ended_at),
    }).then(onClose, (apiError) => setError(getErrorMessage(apiError)));
  };

  return (
    <EntryDialog
      show={show}
      title={isEdit ? 'Edit education' : 'Add education'}
      subtitle="Degrees, diplomas and courses from a school, college or university."
      dirty={dirty}
      canSave={canSave}
      saving={saving}
      saveLabel={isEdit ? 'Save changes' : 'Add education'}
      error={error}
      onSave={handleSave}
      onClose={onClose}
      onDelete={isEdit ? onDelete : null}
      deleteLabel="Delete education"
      deletePrompt={`Delete ${
        education?.qualification || 'this entry'
      }? You can undo straight after.`}
      crossLink={
        onAddQualification && {
          icon: <Award size={18} strokeWidth={2} aria-hidden="true" />,
          prompt: 'Got a certificate or an award rather than a course?',
          label: 'Add it to Certifications and awards',
          onClick: onAddQualification,
        }
      }
    >
      <Field id="education-school" label="School, college or university">
        <input
          id="education-school"
          type="text"
          className={inputClass()}
          value={draft.school}
          onChange={(event) => set({ school: event.target.value })}
          placeholder="For example, Central Saint Martins"
          maxLength={255}
        />
      </Field>
      <Field id="education-course" label="Degree or course">
        <input
          id="education-course"
          type="text"
          className={inputClass()}
          value={draft.course}
          onChange={(event) => set({ course: event.target.value })}
          placeholder="For example, BA Graphic Design"
          maxLength={255}
        />
      </Field>
      <div className={fieldStyles.row}>
        <Field id="education-from" label="From">
          <YearSelect
            id="education-from"
            value={draft.from}
            onChange={(from) => set({ from })}
            placeholder="Select"
          />
        </Field>
        <Field
          id="education-to"
          label="To (or expected)"
          error={endBeforeStart}
        >
          <YearSelect
            id="education-to"
            value={draft.to}
            onChange={(to) => set({ to })}
            ahead={10}
            placeholder="Select"
            error={endBeforeStart}
          />
        </Field>
      </div>
      <Field
        id="education-description"
        label="Description"
        optional
        aside={<CharCount value={draft.content} max={MAX_DESCRIPTION} />}
      >
        <Textarea
          id="education-description"
          className={inputClass()}
          value={draft.content}
          onChange={(event) => set({ content: event.target.value })}
          placeholder="Grade, dissertation, societies: anything a client would care about."
          minRows={3}
          maxRows={12}
          maxLength={MAX_DESCRIPTION}
        />
      </Field>
    </EntryDialog>
  );
};

EducationDialog.propTypes = {
  show: PropTypes.bool,
  education: PropTypes.object,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onAddQualification: PropTypes.func,
};

export default EducationDialog;
